import React, { useState, useEffect } from 'react';
import RefreshIcon from '@mui/icons-material/Refresh';
import axiosInstance from '../../helpers/axios';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import ManagerOverview from './manager/ManagerOverview';
import ManagerTeam from './manager/ManagerTeam';
import ManagerAllocation from './manager/ManagerAllocation';
import ManagerIncentives from './manager/ManagerIncentives';
import ManagerPtp from './manager/ManagerPtp';
import ManagerSettings from './manager/ManagerSettings';
import AgentDashboard from './agent/AgentDashboard';
import AgentLoans from './agent/AgentLoans';
import AgentIncentive from './agent/AgentIncentive';

const MANAGER_TABS = ['Overview', 'Team', 'Allocation', 'Incentives', 'PTP', 'Settings'];
const AGENT_TABS   = ['Dashboard', 'Loans', 'Incentive'];

const fmt = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });

function AgentPeekView({ agent, onExit }) {
  const [activeTab, setActiveTab] = useState(0);

  const renderPeekContent = () => {
    if (activeTab === 0) return <AgentDashboard agentId={agent.agentId} />;
    if (activeTab === 1) return <AgentLoans agentId={agent.agentId} />;
    if (activeTab === 2) return <AgentIncentive agentId={agent.agentId} />;
    return null;
  };

  return (
    <div>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        backgroundColor: '#fff3cd', border: '1px solid #ffc107',
        borderRadius: '8px', padding: '10px 20px', marginBottom: '20px',
      }}>
        <p style={{ margin: 0, fontFamily: 'Inter-Medium', fontSize: '14px', color: '#856404' }}>
          Viewing as: <strong>{agent.agentName}</strong>
        </p>
        <button
          onClick={onExit}
          style={{
            padding: '6px 16px', backgroundColor: '#d32028', color: 'white',
            border: 'none', borderRadius: '6px', fontFamily: 'Inter-Medium',
            fontSize: '13px', cursor: 'pointer',
          }}
        >
          Exit
        </button>
      </div>

      <div style={{ display: 'flex', borderBottom: '2px solid #e0e0e0', marginBottom: '20px' }}>
        {AGENT_TABS.map((tab, index) => (
          <button
            key={tab}
            onClick={() => setActiveTab(index)}
            style={{
              padding: '10px 20px', border: 'none', backgroundColor: 'transparent', cursor: 'pointer',
              fontFamily: 'Inter-Medium', fontSize: '14px',
              fontWeight: activeTab === index ? '600' : '400',
              color: activeTab === index ? '#d32028' : '#667085',
              borderBottom: activeTab === index ? '2px solid #d32028' : '2px solid transparent',
              marginBottom: '-2px',
            }}
          >
            {tab}
          </button>
        ))}
      </div>

      {renderPeekContent()}
    </div>
  );
}

export default function CollectionIncentive() {
  const [activeTab, setActiveTab]     = useState(0);

  const [peekAgent, setPeekAgent]             = useState(null);
  const [showAgentPicker, setShowAgentPicker] = useState(false);
  const [agentPickerList, setAgentPickerList] = useState([]);
  const [pickerLoading, setPickerLoading]     = useState(false);

  // ── Refresh All ──────────────────────────────────────────────────────────────
  const [refreshing, setRefreshing] = useState(false);
  const [toast, setToast]           = useState({ open: false, message: '', type: 'success' });

  // Shared pre-fetched data — populated by refreshAll, passed down as initialData to tabs
  const [sharedDashboard,       setSharedDashboard]       = useState(null);
  const [sharedAgents,          setSharedAgents]          = useState(null);
  const [sharedAnalytics,       setSharedAnalytics]       = useState(null);
  const [sharedPtp,             setSharedPtp]             = useState(null);
  const [sharedRotation,        setSharedRotation]        = useState(null);
  const [sharedIncentivesReport,setSharedIncentivesReport]= useState(null);
  const [sharedAgentDashboard,  setSharedAgentDashboard]  = useState(null);
  const [sharedAgentLoans,      setSharedAgentLoans]      = useState(null);
  const [sharedAgentIncentives, setSharedAgentIncentives] = useState(null);

  const decode    = useCurrentUser();
  const isManager = decode.role === 'admin' || decode.role === 'crm';
  const isAgent   = decode.role === 'collection' || decode.role === 'collection_user';
  const tabs      = isManager ? MANAGER_TABS : AGENT_TABS;

  useEffect(() => {
    if (!toast.open) return;
    const t = setTimeout(
      () => setToast((prev) => ({ ...prev, open: false })),
      toast.type === 'error' ? 3000 : 2000,
    );
    return () => clearTimeout(t);
  }, [toast.open]);

  const refreshAll = async () => {
    if (refreshing) return;
    setRefreshing(true);
    const now = new Date();

    if (isManager) {
      const startDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
      const endDate   = now.toISOString().split('T')[0];
      const repMonth  = now.getMonth() === 0 ? 12 : now.getMonth();
      const repYear   = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();

      const results = await Promise.allSettled([
        axiosInstance.get('/collection-incentive/manager/dashboard'),
        axiosInstance.get('/collection-incentive/manager/agents'),
        axiosInstance.get('/analytics/collection-performance', { params: { startDate, endDate } }),
        axiosInstance.get('/collection-incentive/manager/ptp-tracker'),
        axiosInstance.get('/collection-incentive/manager/rotation-history'),
        axiosInstance.get(`/collection-incentive/manager/incentives/report?month=${repMonth}&year=${repYear}`),
      ]);

      if (results[0].status === 'fulfilled') setSharedDashboard(results[0].value.data.data);
      if (results[1].status === 'fulfilled') setSharedAgents(results[1].value.data.data);
      if (results[2].status === 'fulfilled') setSharedAnalytics(results[2].value.data);
      if (results[3].status === 'fulfilled') setSharedPtp(results[3].value.data.data);
      if (results[4].status === 'fulfilled') setSharedRotation(results[4].value.data.data);
      if (results[5].status === 'fulfilled') setSharedIncentivesReport(results[5].value.data.data);

      const anyFailed = results.some((r) => r.status === 'rejected');
      setToast({
        open: true,
        message: anyFailed ? '⚠ Some data couldn\'t be refreshed' : '✓ Data refreshed',
        type: anyFailed ? 'error' : 'success',
      });
    } else if (isAgent) {
      const results = await Promise.allSettled([
        axiosInstance.get('/collection-incentive/agent/loans'),
        axiosInstance.get('/collection-incentive/agent/dashboard'),
        axiosInstance.get('/collection-incentive/agent/incentives'),
      ]);

      if (results[0].status === 'fulfilled') setSharedAgentLoans(results[0].value.data.data);
      if (results[1].status === 'fulfilled') setSharedAgentDashboard(results[1].value.data.data);
      if (results[2].status === 'fulfilled') setSharedAgentIncentives(results[2].value.data.data);

      const anyFailed = results.some((r) => r.status === 'rejected');
      setToast({
        open: true,
        message: anyFailed ? '⚠ Some data couldn\'t be refreshed' : '✓ Data refreshed',
        type: anyFailed ? 'error' : 'success',
      });
    }

    setRefreshing(false);
  };

  const handleSwitchToAgentView = () => {
    setShowAgentPicker(true);
    if (agentPickerList.length === 0) {
      setPickerLoading(true);
      Promise.all([
        axiosInstance.get('/collection-incentive/manager/agents'),
        axiosInstance.get('/collection-incentive/manager/dashboard'),
      ])
        .then(([agentsRes, dashRes]) => {
          const agentList   = agentsRes.data.data.agents || [];
          const teamSummary = dashRes.data.data.teamSummary || [];
          const summaryMap  = {};
          for (const s of teamSummary) summaryMap[s.agentId] = s;
          setAgentPickerList(agentList.map((a) => ({
            ...a,
            recovered: summaryMap[a.agentId]?.recovered ?? 0,
            ptpCount:  summaryMap[a.agentId]?.ptpCount  ?? 0,
          })));
        })
        .catch(() => {})
        .finally(() => setPickerLoading(false));
    }
  };

  const handleAgentSelect = (agent) => {
    setPeekAgent(agent);
    setShowAgentPicker(false);
  };

  const renderContent = () => {
    if (!isManager && !isAgent) {
      return (
        <div style={{ textAlign: 'center', marginTop: '60px', color: '#667085', fontSize: '16px' }}>
          You do not have permission to view this page.
        </div>
      );
    }
    if (peekAgent) return <AgentPeekView agent={peekAgent} onExit={() => setPeekAgent(null)} />;
    if (isManager) {
      const overviewInit   = sharedDashboard && sharedAnalytics   ? { dashboardRaw: sharedDashboard, analyticsRaw: sharedAnalytics }     : null;
      const teamInit       = sharedDashboard && sharedAgents      ? { dashboardRaw: sharedDashboard, agentsRaw: sharedAgents }            : null;
      const allocInit      = sharedAgents    && sharedRotation    ? { agentsRaw: sharedAgents, rotationHistoryRaw: sharedRotation }       : null;
      const incentivesInit = sharedIncentivesReport               ? { report: sharedIncentivesReport }                                   : null;
      if (activeTab === 0) return <ManagerOverview  initialData={overviewInit} />;
      if (activeTab === 1) return <ManagerTeam      initialData={teamInit} />;
      if (activeTab === 2) return <ManagerAllocation initialData={allocInit} />;
      if (activeTab === 3) return <ManagerIncentives initialData={incentivesInit} />;
      if (activeTab === 4) return <ManagerPtp        initialData={sharedPtp} />;
      if (activeTab === 5) return <ManagerSettings />;
    }
    if (isAgent) {
      const agentDashInit    = sharedAgentDashboard                                        ? { dashboardRaw: sharedAgentDashboard }                                        : null;
      const agentLoansInit   = sharedAgentLoans && sharedAgentDashboard                    ? { loansRaw: sharedAgentLoans, dashboardRaw: sharedAgentDashboard }             : null;
      const agentIncInit     = sharedAgentIncentives && sharedAgentDashboard               ? { incentivesRaw: sharedAgentIncentives, dashboardRaw: sharedAgentDashboard }   : null;
      if (activeTab === 0) return <AgentDashboard initialData={agentDashInit} />;
      if (activeTab === 1) return <AgentLoans     initialData={agentLoansInit} />;
      if (activeTab === 2) return <AgentIncentive initialData={agentIncInit} />;
    }
    return null;
  };

  return (
    <div style={{ width: '100%', backgroundColor: 'white', minHeight: '100vh' }}>

        {/* ── Spin keyframe for refresh icon ── */}
        <style>{`@keyframes ci-spin { to { transform: rotate(360deg) } }`}</style>

        {/* ── Page header: title centred, buttons right-aligned ── */}
        <div style={{ display: 'flex', alignItems: 'center', marginTop: '4px', marginBottom: '10px' }}>
          <div style={{ flex: 1 }} />
          <p style={{ margin: 0, fontFamily: 'Inter-Medium', color: '#667085', fontSize: '22px', whiteSpace: 'nowrap' }}>
            Collection Incentive
          </p>
          <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '8px' }}>
            {isManager && !peekAgent && (
              <button
                onClick={handleSwitchToAgentView}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '35px',
                  padding: '0 16px',
                  backgroundColor: '#D22129',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontFamily: 'Inter-Medium',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#b71c1c'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#D22129'; }}
              >
                Switch to Agent View
              </button>
            )}
            <button
              onClick={refreshAll}
              disabled={refreshing}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '35px',
                padding: '0 16px',
                backgroundColor: refreshing ? '#e57373' : '#D22129',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                cursor: refreshing ? 'not-allowed' : 'pointer',
              }}
            >
              <RefreshIcon style={{ animation: refreshing ? 'ci-spin 0.8s linear infinite' : 'none' }} />
            </button>
          </div>
        </div>

        {/* Tab bar — hidden while in peek mode */}
        {!peekAgent && (
          <div style={{ display: 'flex', borderBottom: '2px solid #e0e0e0', marginBottom: '12px' }}>
            {tabs.map((tab, index) => (
              <button
                key={tab}
                onClick={() => setActiveTab(index)}
                style={{
                  padding: '10px 20px', border: 'none', backgroundColor: 'transparent', cursor: 'pointer',
                  fontFamily: 'Inter-Medium', fontSize: '14px',
                  fontWeight: activeTab === index ? '600' : '400',
                  color: activeTab === index ? '#d32028' : '#667085',
                  borderBottom: activeTab === index ? '2px solid #d32028' : '2px solid transparent',
                  marginBottom: '-2px',
                }}
              >
                {tab}
              </button>
            ))}
          </div>
        )}

        {renderContent()}

      {/* ── Refresh toast ── */}
      {toast.open && (
        <div style={{
          position: 'fixed', top: '20px', right: '20px', zIndex: 9999,
          padding: '10px 16px', borderRadius: '8px',
          backgroundColor: toast.type === 'success' ? '#ecfdf5' : '#fef2f2',
          border: `1px solid ${toast.type === 'success' ? '#a7f3d0' : '#fecaca'}`,
          color: toast.type === 'success' ? '#065f46' : '#991b1b',
          fontSize: '13px', fontWeight: '600', fontFamily: 'Inter-Medium',
          boxShadow: '0 4px 12px rgba(0,0,0,0.10)',
          pointerEvents: 'none',
        }}>
          {toast.message}
        </div>
      )}

      {/* ── Agent Picker Modal ── */}
      {showAgentPicker && (
        <div
          onClick={() => setShowAgentPicker(false)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(10, 15, 30, 0.65)',
            backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white',
              borderRadius: '18px',
              width: '460px',
              boxShadow: '0 20px 50px rgba(0,0,0,0.18)',
              overflow: 'hidden',
              maxHeight: '85vh',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Gradient modal header — compact */}
            <div style={{
              background: 'linear-gradient(135deg, #b71c1c 0%, #d32028 100%)',
              padding: '18px 20px',
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <div>
                <p style={{ margin: 0, fontSize: '17px', fontWeight: '700', color: 'white', fontFamily: 'Inter-Medium' }}>
                  Switch to Agent View
                </p>
                <p style={{ margin: '3px 0 0', fontSize: '12px', color: 'rgba(255,255,255,0.75)', fontFamily: 'Inter-Medium' }}>
                  {pickerLoading ? 'Loading...' : `${agentPickerList.length} agent${agentPickerList.length !== 1 ? 's' : ''} available`}
                </p>
              </div>
              <button
                onClick={() => setShowAgentPicker(false)}
                style={{
                  background: 'rgba(255,255,255,0.2)', border: 'none',
                  borderRadius: '50%', width: '28px', height: '28px',
                  cursor: 'pointer', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', color: 'white', fontSize: '13px', flexShrink: 0,
                }}
              >
                ✕
              </button>
            </div>

            {/* Agent cards list */}
            <div style={{ overflowY: 'auto', padding: '20px 20px 16px', flex: 1 }}>
              {pickerLoading ? (
                <div style={{ textAlign: 'center', padding: '40px 0', color: '#667085', fontFamily: 'Inter-Medium', fontSize: '14px' }}>
                  Loading agents...
                </div>
              ) : agentPickerList.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 0', color: '#667085', fontFamily: 'Inter-Medium', fontSize: '14px' }}>
                  No agents found
                </div>
              ) : (
                agentPickerList.map((agent) => {
                  const isActive = agent.availability === 'Active';
                  return (
                    <div
                      key={agent.agentId}
                      onClick={() => handleAgentSelect(agent)}
                      style={{
                        display: 'flex', alignItems: 'center',
                        marginBottom: '8px',
                        borderRadius: '10px',
                        cursor: 'pointer',
                        border: '1px solid #ebebeb',
                        backgroundColor: 'white',
                        overflow: 'hidden',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#d32028'; e.currentTarget.style.backgroundColor = '#fff8f8'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#ebebeb'; e.currentTarget.style.backgroundColor = 'white'; }}
                    >
                      {/* Status colour bar */}
                      <div style={{
                        width: '4px', alignSelf: 'stretch', flexShrink: 0,
                        backgroundColor: isActive ? '#d32028' : '#bdbdbd',
                      }} />

                      {/* Avatar */}
                      <div style={{
                        width: '36px', height: '36px', borderRadius: '50%', margin: '10px 12px 10px 14px', flexShrink: 0,
                        background: isActive
                          ? 'linear-gradient(135deg, #d32028 0%, #ff6b6b 100%)'
                          : 'linear-gradient(135deg, #667085 0%, #98a2b3 100%)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <span style={{ color: 'white', fontSize: '14px', fontWeight: '700', fontFamily: 'Inter-Medium' }}>
                          {agent.agentName ? agent.agentName.charAt(0).toUpperCase() : '?'}
                        </span>
                      </div>

                      {/* Name + status */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: '#101828', fontFamily: 'Inter-Medium' }}>
                          {agent.agentName}
                        </p>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px' }}>
                          <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: isActive ? '#22c55e' : '#9e9e9e', display: 'inline-block' }} />
                          <span style={{ fontSize: '10px', fontFamily: 'Inter-Medium', fontWeight: '600', color: isActive ? '#16a34a' : '#9e9e9e' }}>
                            {isActive ? 'Active' : 'Away'}
                          </span>
                        </div>
                      </div>

                      {/* Stats */}
                      <div style={{ display: 'flex' }}>
                        <div style={{ textAlign: 'center', padding: '0 14px', borderLeft: '1px solid #f0f0f0' }}>
                          <p style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#101828', fontFamily: 'Inter-Medium' }}>
                            {agent.loansAssigned ?? 0}
                          </p>
                          <p style={{ margin: 0, fontSize: '9px', color: '#98a2b3', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                            Loans
                          </p>
                        </div>
                        <div style={{ textAlign: 'center', padding: '0 14px', borderLeft: '1px solid #f0f0f0' }}>
                          <p style={{ margin: 0, fontSize: '13px', fontWeight: '700', fontFamily: 'Inter-Medium', color: agent.recovered > 0 ? '#16a34a' : '#667085' }}>
                            {agent.recovered > 0 ? fmt(agent.recovered) : '—'}
                          </p>
                          <p style={{ margin: 0, fontSize: '9px', color: '#98a2b3', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                            Recovery
                          </p>
                        </div>
                      </div>

                      {/* Arrow */}
                      <span style={{ color: '#d32028', fontSize: '20px', fontWeight: '700', margin: '0 14px 0 10px', lineHeight: 1 }}>›</span>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
